if (!customElements.get('nc-seed-product')) {
  customElements.define('nc-seed-product', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.quantity = this.querySelector('[data-quantity]');
      this.form = this.querySelector('form');
      this.gallery = this.querySelector('[data-gallery]');
      this.track = this.querySelector('[data-ingredient-track]');
      this.sticky = this.querySelector('[data-sticky]');
      if (!this.quantity || !this.form || !this.gallery || !this.track) return;
      this.readQuantity = () => {
        const value = new URL(location.href).searchParams.get('quantity');
        this.quantity.value = [...this.quantity.options].some(option => option.value === value) ? value : '1';
        this.updateQuantity(false);
      };
      this.readQuantity();
      this.initDocuments(options);
      this.quantity.addEventListener('change', () => this.updateQuantity(true), options);
      window.addEventListener('popstate', this.readQuantity, options);
      this.form.addEventListener('submit', () => this.syncQuantity(), { ...options, capture: true });
      this.addEventListener('click', event => {
        const packageLink = event.target.closest('[data-package]');
        if (packageLink) {
          this.quantity.value = packageLink.dataset.package;
          this.updateQuantity(true);
          this.quantity.focus({ preventScroll: true });
        }
      }, options);
      const slides = [...this.gallery.children];
      let current = 0;
      const showSlide = index => {
        const slide = slides[Math.max(0, Math.min(slides.length - 1, index))];
        this.gallery.scrollTo({ left: slide.offsetLeft - slides[0].offsetLeft });
      };
      const updateGallery = () => {
        current = slides.reduce((best, slide, index) => Math.abs(slide.offsetLeft - slides[0].offsetLeft - this.gallery.scrollLeft) < Math.abs(slides[best].offsetLeft - slides[0].offsetLeft - this.gallery.scrollLeft) ? index : best, 0);
      };
      this.gallery.addEventListener('scroll', updateGallery, { ...options, passive: true });
      this.gallery.addEventListener('keydown', event => {
        if (event.target !== this.gallery || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        showSlide(current + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      updateGallery();
      this.querySelector('[data-ingredient-controls]').hidden = false;
      const moveIngredients = direction => this.track.scrollBy({ left: direction * (this.track.firstElementChild.getBoundingClientRect().width + 16) });
      this.querySelector('[data-ingredient-prev]').addEventListener('click', () => moveIngredients(-1), options);
      this.querySelector('[data-ingredient-next]').addEventListener('click', () => moveIngredients(1), options);
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        moveIngredients(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      const updateIngredients = () => {
        this.querySelector('[data-ingredient-prev]').disabled = this.track.scrollLeft <= 1;
        this.querySelector('[data-ingredient-next]').disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      };
      this.track.addEventListener('scroll', updateIngredients, { ...options, passive: true });
      this.resizeObserver = new ResizeObserver(() => { updateIngredients(); updateGallery(); });
      this.resizeObserver.observe(this.track);
      this.resizeObserver.observe(this.gallery);
      const purchaseButton = this.form.querySelector('.ns-add');
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.sticky.hidden = false;
      this.updateSticky = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          const visible = purchaseButton.getBoundingClientRect().bottom <= 0;
          this.sticky.classList.toggle('is-visible', visible);
          this.sticky.setAttribute('aria-hidden', String(!visible));
          this.sticky.inert = !visible;
          this.stickyFrame = null;
        });
      };
      this.querySelectorAll('[data-sticky-buy]').forEach(link => link.addEventListener('click', event => {
        event.preventDefault();
        this.quantity.focus({ preventScroll: true });
        this.form.scrollIntoView({ behavior: motion.matches ? 'instant' : 'smooth', block: 'start' });
      }, options));
      window.addEventListener('scroll', this.updateSticky, { ...options, passive: true });
      window.addEventListener('resize', this.updateSticky, options);
      this.stickyResizeObserver = new ResizeObserver(this.updateSticky);
      this.stickyResizeObserver.observe(this.form);
      this.updateSticky();
      this.initResearch(options);
      this.initComparison(options);
      this.initBenefitTimeline(options);
      this.initStory(options);
      this.initPack(options);
      this.initBundle(options);
    }
    initComparison(options) {
      const scroll = this.querySelector('[data-compare-scroll]');
      const bar = this.querySelector('[data-compare-scrollbar]');
      if (!scroll || !bar) return;
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const update = () => {
        const max = scroll.scrollWidth - scroll.clientWidth;
        bar.hidden = max <= 1;
        scroll.style.cursor = max > 1 ? '' : 'auto';
        if (max <= 1) return;
        const thumb = bar.clientWidth * scroll.clientWidth / scroll.scrollWidth;
        bar.style.setProperty('--ns-scroll-thumb', `${thumb}px`);
        bar.style.setProperty('--ns-scroll-offset', `${(bar.clientWidth - thumb) * Math.max(0, Math.min(1, scroll.scrollLeft / max))}px`);
      };
      scroll.addEventListener('scroll', update, { ...options, passive: true });
      scroll.addEventListener('keydown', event => {
        if (event.target !== scroll || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const left = event.key === 'Home' ? 0 : event.key === 'End' ? scroll.scrollWidth : scroll.scrollLeft + (event.key === 'ArrowRight' ? 1 : -1) * scroll.clientWidth * .65;
        scroll.scrollTo({ left, behavior: motion.matches ? 'auto' : 'smooth' });
      }, options);
      let drag = null;
      scroll.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || scroll.scrollWidth <= scroll.clientWidth) return;
        drag = { id: event.pointerId, x: event.clientX, left: scroll.scrollLeft };
        scroll.setPointerCapture(event.pointerId);
      }, options);
      scroll.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const delta = event.clientX - drag.x;
        if (Math.abs(delta) > 4) scroll.classList.add('is-dragging');
        scroll.scrollLeft = drag.left - delta;
      }, options);
      const finish = () => { drag = null; scroll.classList.remove('is-dragging'); };
      scroll.addEventListener('pointerup', finish, options);
      scroll.addEventListener('pointercancel', finish, options);
      scroll.addEventListener('lostpointercapture', finish, options);
      this.compareResizeObserver = new ResizeObserver(update);
      this.compareResizeObserver.observe(scroll);
      this.compareResizeObserver.observe(scroll.firstElementChild);
      update();
    }
    initBenefitTimeline(options) {
      const timeline = this.querySelector('[data-benefit-timeline]');
      if (!timeline) return;
      const track = timeline.querySelector('.ns-benefit-steps');
      const steps = [...track.children];
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const paint = () => {
        this.benefitFrame = null;
        if (!timeline.isConnected) return;
        const top = track.getBoundingClientRect().top + 14;
        const last = steps[steps.length - 1].getBoundingClientRect().top + 14;
        const length = Math.max(0, last - top);
        const threshold = window.innerHeight * .72;
        track.style.setProperty('--ns-benefit-length', `${length}px`);
        track.style.setProperty('--ns-benefit-progress', `${motion.matches ? length : Math.max(0, Math.min(length, threshold - top))}px`);
        steps.forEach(step => {
          const distance = threshold - step.getBoundingClientRect().top;
          const opacity = motion.matches ? 1 : .28 + .72 * Math.max(0, Math.min(1, distance / 100));
          step.style.setProperty('--ns-benefit-opacity', opacity.toFixed(3));
        });
      };
      const update = () => {
        if (this.benefitFrame == null) this.benefitFrame = requestAnimationFrame(paint);
      };
      timeline.classList.add('ns-benefit-timeline-enhanced');
      window.addEventListener('scroll', update, { ...options, passive: true });
      window.addEventListener('resize', update, options);
      motion.addEventListener('change', update, options);
      this.benefitResizeObserver = new ResizeObserver(update);
      this.benefitResizeObserver.observe(track);
      paint();
    }
    initBundle(options) {
      const bundle = this.querySelector('.ns-seed-bundle');
      if (!bundle) return;
      const products = [...bundle.querySelectorAll('.ns-bundle-product')];
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const clamp = value => Math.max(0, Math.min(1, value));
      const paint = () => {
        this.bundleFrame = null;
        if (!bundle.isConnected) return;
        const rect = bundle.getBoundingClientRect();
        const height = window.innerHeight;
        const progress = motion.matches ? 1 : clamp((height * .9 - rect.top) / (height * .55));
        const inset = 16 * (1 - progress);
        bundle.style.setProperty('--ns-bundle-scale', String(1 - inset * 2 / Math.max(1, rect.width)));
        bundle.style.setProperty('--ns-bundle-radius', `${32 * (1 - progress)}px`);
        products.forEach(product => {
          const reveal = motion.matches ? 1 : clamp((height * .93 - product.getBoundingClientRect().top) / (height * .24));
          product.style.setProperty('--ns-product-reveal', reveal.toFixed(3));
          product.style.setProperty('--ns-label-y', `${8 * (1 - reveal)}px`);
          product.style.setProperty('--ns-image-y', `${18 * (1 - reveal)}px`);
          product.style.setProperty('--ns-image-opacity', String(.5 + reveal * .5));
        });
      };
      const update = () => {
        if (this.bundleFrame == null) this.bundleFrame = requestAnimationFrame(paint);
      };
      window.addEventListener('scroll', update, { ...options, passive: true });
      window.addEventListener('resize', update, options);
      motion.addEventListener('change', update, options);
      this.bundleResizeObserver = new ResizeObserver(update);
      this.bundleResizeObserver.observe(bundle);
      products.forEach(product => this.bundleResizeObserver.observe(product));
      paint();
      bundle.classList.add('ns-bundle-scroll-motion');
    }
    initPack(options) {
      const card = this.querySelector('.ns-pack-card');
      const pack = card?.querySelector('[data-pack-interact]');
      const photo = pack?.querySelector('img');
      if (!card || !pack || !photo) return;
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
      let inView = false;
      let played = false;
      const resetTilt = () => {
        pack.style.removeProperty('--ns-pack-x');
        pack.style.removeProperty('--ns-pack-y');
      };
      const stop = () => {
        clearTimeout(this.packAutoTimer);
        this.packAutoTimer = null;
        this.packShake?.cancel();
        resetTilt();
      };
      const scheduleShake = () => {
        if (played || this.packAutoTimer != null || !inView || motion.matches || document.hidden || !photo.complete || !photo.naturalWidth) return;
        this.packAutoTimer = setTimeout(() => {
          this.packAutoTimer = null;
          if (!inView || motion.matches || document.hidden) return;
          played = true;
          this.packShake = photo.animate([
            { transform: 'translateY(0) rotate(0deg)' },
            { transform: 'translateY(-7px) rotate(-7deg)' },
            { transform: 'translateY(-4px) rotate(7deg)' },
            { transform: 'translateY(-6px) rotate(-5deg)' },
            { transform: 'translateY(-3px) rotate(4deg)' },
            { transform: 'translateY(0) rotate(0deg)' }
          ], { duration: 2800, easing: 'ease-in-out' });
        }, 1400);
      };
      const updateMotion = () => {
        if (motion.matches) {
          stop();
          card.classList.remove('ns-pack-motion');
          card.classList.add('ns-pack-visible');
        } else scheduleShake();
      };
      if ('IntersectionObserver' in window) {
        if (!motion.matches) card.classList.add('ns-pack-motion');
        this.packObserver = new IntersectionObserver(entries => {
          const entry = entries[entries.length - 1];
          if (entry.intersectionRatio >= 0.6) inView = true;
          else if (!entry.isIntersecting || entry.intersectionRatio < 0.15) inView = false;
          if (inView) {
            card.classList.add('ns-pack-visible');
            scheduleShake();
          } else {
            stop();
            played = false;
          }
        }, { threshold: [0, 0.15, 0.6] });
        this.packObserver.observe(pack);
      }
      updateMotion();
      motion.addEventListener('change', updateMotion, options);
      photo.addEventListener('load', scheduleShake, options);
      document.addEventListener('visibilitychange', () => document.hidden ? stop() : scheduleShake(), options);
      pack.addEventListener('pointermove', event => {
        if (motion.matches || !hover.matches || event.pointerType !== 'mouse') return;
        const bounds = pack.getBoundingClientRect();
        const x = Math.max(-1, Math.min(1, (event.clientX - bounds.left) / bounds.width * 2 - 1));
        const y = Math.max(-1, Math.min(1, (event.clientY - bounds.top) / bounds.height * 2 - 1));
        pack.style.setProperty('--ns-pack-x', (-y * 5).toFixed(2) + 'deg');
        pack.style.setProperty('--ns-pack-y', (x * 7).toFixed(2) + 'deg');
      }, options);
      pack.addEventListener('pointerleave', resetTilt, options);
    }
    initStory(options) {
      const track = this.querySelector('[data-story-track]');
      if (!track) return;
      const cards = [...track.children];
      const dots = [...this.querySelectorAll('[data-story-dot]')];
      const controls = this.querySelector('[data-story-controls]');
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let current = 0;
      const update = () => {
        const overflow = track.scrollWidth > track.clientWidth + 2;
        controls.hidden = !overflow;
        track.tabIndex = overflow ? 0 : -1;
        current = cards.reduce((best, card, i) => Math.abs(card.offsetLeft - cards[0].offsetLeft - track.scrollLeft) < Math.abs(cards[best].offsetLeft - cards[0].offsetLeft - track.scrollLeft) ? i : best, 0);
        dots.forEach((dot, i) => i === current ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current'));
      };
      const show = index => {
        const card = cards[Math.max(0, Math.min(cards.length - 1, index))];
        track.scrollTo({ left: card.offsetLeft - cards[0].offsetLeft, behavior: motion.matches ? 'instant' : 'smooth' });
      };
      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i), options));
      track.addEventListener('scroll', update, { ...options, passive: true });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        show(event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.storyResizeObserver = new ResizeObserver(update);
      this.storyResizeObserver.observe(track);
      update();
    }
    initResearch(options) {
      const research = this.querySelector('[data-research]');
      if (!research) return;
      const tabs = [...research.querySelectorAll('[data-research-tab]')];
      const panels = [...research.querySelectorAll('[data-research-panel]')];
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let current = 0;
      let visible = false;
      const stop = () => {
        this.researchAnimations?.forEach(animation => animation.cancel());
        this.researchAnimations = [];
      };
      const animate = () => {
        stop();
        if (!visible || motion.matches) return;
        this.researchAnimations = [...panels[current].querySelectorAll('.ns-research-bar')].map(bar => bar.animate([
          { transform: 'scaleY(0)' }, { transform: 'scaleY(1)' }
        ], { duration: 1100, easing: 'cubic-bezier(.2,.7,.2,1)' }));
      };
      const select = (index, announce = true) => {
        current = index;
        tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === current));
          tab.tabIndex = i === current ? 0 : -1;
          panels[i].hidden = i !== current;
        });
        if (announce) research.querySelector('[data-research-status]').textContent = panels[current].querySelector('figcaption p').textContent;
        animate();
      };
      tabs.forEach((tab, index) => {
        panels[index].setAttribute('role', 'tabpanel');
        panels[index].setAttribute('aria-labelledby', tab.id);
        panels[index].tabIndex = 0;
        tab.addEventListener('click', () => { if (current !== index) select(index); }, options);
        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          select(next);
          tabs[next].focus();
        }, options);
      });
      research.querySelector('[data-research-tabs]').hidden = false;
      select(0, false);
      if ('IntersectionObserver' in window) {
        this.researchObserver = new IntersectionObserver(entries => {
          visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.3);
          if (visible) { animate(); this.researchObserver.disconnect(); }
        }, { threshold: [0, 0.3] });
        this.researchObserver.observe(research);
      } else visible = true;
      motion.addEventListener('change', stop, options);
    }
    initDocuments(options) {
      const dialog = this.querySelector('[data-document-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const image = dialog.querySelector('[data-document-image]');
      const body = dialog.querySelector('[data-document-body]');
      const title = dialog.querySelector('[data-document-title]');
      const original = dialog.querySelector('[data-document-original]');
      const zoom = dialog.querySelector('[data-document-zoom]');
      const stack = dialog.querySelector('[data-document-pages]');
      const dimensions = [[2084,2303],[2084,3391],[2084,1497],[2084,2898],[2084,3268],[2085,3337],[2085,2360],[2085,2129],[2084,2757],[2084,2801],[2084,4659],[2084,3475],[2085,3856],[2085,4679],[2085,4865],[2085,1878]];
      const flyerPages = [...this.querySelectorAll('.ns-brochure-links a')].map((link, index) => ({ src: link.href, alt: link.textContent.replace('↗', '').trim(), size: dimensions[index] }));
      const label = this.querySelector('.ns-label-link img');
      let opener = null, previousOverflow = '', activeDocument = image;
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-open-document], [data-open-information]');
        if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const flyer = trigger.dataset.openDocument === 'flyer';
        if (flyer && !flyerPages.length) return;
        event.preventDefault();
        opener = trigger;
        title.textContent = flyer ? dialog.dataset.flyerTitle : dialog.dataset.labelTitle;
        if (flyer && !stack.children.length) {
          const fragment = document.createDocumentFragment();
          flyerPages.forEach((page, index) => {
            const pageImage = document.createElement('img');
            pageImage.width = page.size[0];
            pageImage.height = page.size[1];
            pageImage.alt = page.alt;
            pageImage.loading = index === 0 ? 'eager' : 'lazy';
            pageImage.decoding = 'async';
            pageImage.src = page.src;
            fragment.append(pageImage);
          });
          stack.append(fragment);
        }
        if (!flyer) {
          image.src = label.src;
          image.alt = label.alt;
          original.href = label.src;
        }
        stack.hidden = !flyer;
        image.hidden = flyer;
        original.hidden = flyer;
        body.classList.toggle('is-flyer', flyer);
        activeDocument = flyer ? stack : image;
        activeDocument.classList.remove('is-zoomed');
        zoom.setAttribute('aria-pressed', 'false');
        dialog.querySelector('[data-document-status]').textContent = title.textContent;
        previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        dialog.showModal();
        body.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }, options);
      this.querySelectorAll('[data-open-document], [data-open-information]').forEach(trigger => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', dialog.id);
        trigger.setAttribute('role', 'button');
        trigger.addEventListener('keydown', event => {
          if (event.key === ' ') { event.preventDefault(); trigger.click(); }
        }, options);
      });
      dialog.querySelector('[data-document-close]').addEventListener('click', () => dialog.close(), options);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }, options);
      this.releaseDocument = () => {
        document.documentElement.style.overflow = previousOverflow;
        opener?.focus({ preventScroll: true });
      };
      dialog.addEventListener('close', this.releaseDocument, options);
      zoom.addEventListener('click', () => {
        const enlarged = activeDocument.classList.toggle('is-zoomed');
        zoom.setAttribute('aria-pressed', String(enlarged));
      }, options);
    }
    syncQuantity() {
      if (this.syncing) return;
      this.syncing = true;
      const radio = this.querySelector(`.bundler-volume-target-element[data-bundle="865741"] input[name="bundle_quantity"][value="${this.quantity.value}"]`);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.syncing = false;
    }
    updateQuantity(updateUrl) {
      const option = this.quantity.selectedOptions[0];
      if (!option) return;
      this.querySelector('.ns-price-summary [data-price]').textContent = option.dataset.price;
      this.querySelector('.ns-price-summary [data-unit]').textContent = option.value === '1' ? '' : option.dataset.unit;
      this.querySelector('.ns-price-summary [data-days]').textContent = option.dataset.days;
      this.syncQuantity();
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('quantity', option.value);
        history.replaceState({}, '', url);
      }
    }
    disconnectedCallback() {
      const documentDialog = this.querySelector('[data-document-dialog]');
      if (documentDialog?.open) { documentDialog.close(); this.releaseDocument?.(); }
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = null;
      this.stickyResizeObserver?.disconnect();
      if (this.sticky) {
        this.sticky.hidden = true;
        this.sticky.inert = true;
        this.sticky.classList.remove('is-visible');
        this.sticky.setAttribute('aria-hidden', 'true');
      }
      this.resizeObserver?.disconnect();
      this.compareResizeObserver?.disconnect();
      this.querySelector('[data-compare-scroll]')?.classList.remove('is-dragging');
      this.benefitResizeObserver?.disconnect();
      cancelAnimationFrame(this.benefitFrame);
      this.benefitFrame = null;
      this.querySelector('[data-benefit-timeline]')?.classList.remove('ns-benefit-timeline-enhanced');
      this.storyResizeObserver?.disconnect();
      this.bundleResizeObserver?.disconnect();
      cancelAnimationFrame(this.bundleFrame);
      this.bundleFrame = null;
      const bundle = this.querySelector('.ns-seed-bundle');
      bundle?.classList.remove('ns-bundle-scroll-motion');
      bundle?.style.removeProperty('--ns-bundle-scale');
      bundle?.style.removeProperty('--ns-bundle-radius');
      this.packObserver?.disconnect();
      clearTimeout(this.packAutoTimer);
      this.packAutoTimer = null;
      this.packShake?.cancel();
      this.querySelector('.ns-pack-card')?.classList.remove('ns-pack-motion', 'ns-pack-visible');
      this.researchObserver?.disconnect();
      this.researchAnimations?.forEach(animation => animation.cancel());
      this.querySelector('[data-research-tabs]')?.setAttribute('hidden', '');
      this.querySelectorAll('[data-research-panel]').forEach(panel => { panel.hidden = false; panel.removeAttribute('role'); panel.removeAttribute('aria-labelledby'); panel.removeAttribute('tabindex'); });
    }
  });
}
